import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Mail,
  Play,
  Plug,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Inbox,
  Clock,
  Save,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_META = {
  success: { label: 'Succès', color: 'bg-green-100 text-green-800', Icon: CheckCircle2 },
  no_email: { label: 'Aucun email', color: 'bg-amber-100 text-amber-800', Icon: AlertCircle },
  error: { label: 'Erreur', color: 'bg-red-100 text-red-800', Icon: XCircle },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const MedicielAutoTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [passwordInput, setPasswordInput] = useState('');

  const authHeader = () => {
    const token = localStorage.getItem('dermascan_token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API_URL}/mediciel-auto/settings`, { headers: authHeader() }),
        axios.get(`${API_URL}/mediciel-auto/logs?limit=30`, { headers: authHeader() }),
      ]);
      setSettings(s.data);
      setLogs(l.data || []);
    } catch (e) {
      toast.error("Impossible de charger la configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const updateField = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const persistForm = async () => {
    const payload = {
      enabled: settings.enabled,
      imap_host: settings.imap_host,
      imap_port: parseInt(settings.imap_port, 10) || 993,
      imap_user: (settings.imap_user || '').trim(),
      sender_filter: (settings.sender_filter || '').trim(),
      subject_prefix: (settings.subject_prefix || '').trim(),
      schedule_hour: parseInt(settings.schedule_hour, 10),
      schedule_minute: parseInt(settings.schedule_minute, 10),
      notify_no_email: !!settings.notify_no_email,
    };
    if (passwordInput.trim()) {
      payload.imap_password = passwordInput.trim();
    }
    const res = await axios.put(
      `${API_URL}/mediciel-auto/settings`,
      payload,
      { headers: authHeader() },
    );
    setSettings(res.data);
    setPasswordInput('');
    return res.data;
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await persistForm();
      toast.success('Configuration enregistrée');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!settings) return;
    setTesting(true);
    try {
      // Always persist first so the form is the source of truth
      const saved = await persistForm();
      if (!saved.imap_user || !saved.imap_password_set) {
        toast.error("Renseignez l'adresse Gmail et le mot de passe d'application avant de tester");
        return;
      }
      const res = await axios.post(
        `${API_URL}/mediciel-auto/test-connection`,
        {},
        { headers: authHeader() },
      );
      if (res.data.ok) {
        toast.success('Connexion IMAP OK ✓');
      } else {
        toast.error(`Échec: ${res.data.error || 'Erreur inconnue'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setTesting(false);
    }
  };

  const runNow = async () => {
    if (!settings) return;
    setRunning(true);
    try {
      // Persist form before running so the user doesn't have to click Save first
      await persistForm();
      const res = await axios.post(
        `${API_URL}/mediciel-auto/run-now`,
        {},
        { headers: authHeader() },
      );
      const status = res.data.status;
      if (status === 'success') {
        toast.success(res.data.message || 'Import réussi');
      } else if (status === 'no_email') {
        toast.warning(res.data.message || 'Aucun email Mediciel trouvé');
      } else {
        toast.error(res.data.message || 'Erreur');
      }
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du déclenchement");
    } finally {
      setRunning(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="text-center py-12 text-gray-500">
        <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
        Chargement…
      </div>
    );
  }

  const statusMeta = STATUS_META[settings.last_run_status] || null;

  return (
    <div className="space-y-6" data-testid="mediciel-auto-tab">
      {/* Status banner */}
      <Card className="border-l-4 border-[#2E7D32]">
        <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-5 h-5 text-[#2E7D32]" />
              <h3 className="font-semibold text-lg">Auto-import Mediciel par email</h3>
              <Badge className={settings.enabled ? 'bg-green-600' : 'bg-gray-400'}>
                {settings.enabled ? 'Actif' : 'Désactivé'}
              </Badge>
            </div>
            <p className="text-sm text-gray-600">
              Programmé tous les jours à&nbsp;
              <b>{String(settings.schedule_hour).padStart(2, '0')}:{String(settings.schedule_minute).padStart(2, '0')}</b> UTC.
              {statusMeta && (
                <>
                  {' '}Dernière exécution :{' '}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${statusMeta.color}`}>
                    <statusMeta.Icon className="w-3 h-3" />
                    {statusMeta.label}
                  </span>{' '}
                  ({fmtDate(settings.last_run_at)})
                </>
              )}
            </p>
            {settings.last_run_message && (
              <p className="text-xs text-gray-500 mt-1">{settings.last_run_message}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={!!settings.enabled}
              onCheckedChange={(v) => updateField('enabled', v)}
              data-testid="mediciel-enabled-switch"
            />
            <span className="text-sm">{settings.enabled ? 'Actif' : 'Désactivé'}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-[#2E7D32]" />
              Connexion IMAP (Gmail / Workspace)
            </CardTitle>
            <CardDescription>
              Le mot de passe doit être un App Password Google (16 caractères).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Serveur IMAP</Label>
              <Input
                value={settings.imap_host || ''}
                onChange={(e) => updateField('imap_host', e.target.value)}
                data-testid="mediciel-imap-host"
              />
            </div>
            <div>
              <Label>Port</Label>
              <Input
                type="number"
                value={settings.imap_port || 993}
                onChange={(e) => updateField('imap_port', e.target.value)}
                data-testid="mediciel-imap-port"
              />
            </div>
            <div>
              <Label>Adresse Gmail à surveiller</Label>
              <Input
                placeholder="irie.nathalie@phciesada.org"
                value={settings.imap_user || ''}
                onChange={(e) => updateField('imap_user', e.target.value)}
                data-testid="mediciel-imap-user"
              />
            </div>
            <div>
              <Label>
                App Password Google
                {settings.imap_password_set && (
                  <Badge className="ml-2 bg-green-100 text-green-800">Enregistré</Badge>
                )}
              </Label>
              <Input
                type="password"
                placeholder={settings.imap_password_set ? 'Laisser vide pour ne pas changer' : 'xxxx xxxx xxxx xxxx'}
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                data-testid="mediciel-imap-password"
              />
              <p className="text-xs text-gray-500 mt-1">
                Compte Google → Sécurité → Validation en 2 étapes →
                Mots de passe d'application.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Filters & schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#2E7D32]" />
              Filtres & planification
            </CardTitle>
            <CardDescription>
              Le job ne traite que les emails non lus correspondant aux filtres.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Expéditeur attendu</Label>
              <Input
                value={settings.sender_filter || ''}
                onChange={(e) => updateField('sender_filter', e.target.value)}
                data-testid="mediciel-sender-filter"
              />
            </div>
            <div>
              <Label>Préfixe du sujet</Label>
              <Input
                value={settings.subject_prefix || ''}
                onChange={(e) => updateField('subject_prefix', e.target.value)}
                data-testid="mediciel-subject-prefix"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Heure (UTC = Abidjan)</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={settings.schedule_hour ?? 8}
                  onChange={(e) => updateField('schedule_hour', e.target.value)}
                  data-testid="mediciel-hour"
                />
              </div>
              <div>
                <Label>Minute</Label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={settings.schedule_minute ?? 0}
                  onChange={(e) => updateField('schedule_minute', e.target.value)}
                  data-testid="mediciel-minute"
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div>
                <p className="font-medium text-sm">M'avertir par email si aucun email Mediciel</p>
                <p className="text-xs text-gray-600">
                  Alerte automatique après 3 jours consécutifs sans email, quel que soit ce réglage.
                </p>
              </div>
              <Switch
                checked={!!settings.notify_no_email}
                onCheckedChange={(v) => updateField('notify_no_email', v)}
                data-testid="mediciel-notify-no-email"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#2E7D32] hover:bg-[#1B5E20]"
          data-testid="mediciel-save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
        <Button
          variant="outline"
          onClick={testConnection}
          disabled={testing}
          data-testid="mediciel-test-btn"
        >
          <Plug className="w-4 h-4 mr-2" />
          {testing ? 'Test en cours…' : 'Tester la connexion'}
        </Button>
        <Button
          variant="outline"
          onClick={runNow}
          disabled={running}
          data-testid="mediciel-run-now-btn"
        >
          <Play className="w-4 h-4 mr-2" />
          {running ? 'Exécution…' : 'Lancer maintenant'}
        </Button>
        <Button variant="ghost" onClick={loadAll} data-testid="mediciel-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Rafraîchir
        </Button>
      </div>

      {/* Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des exécutions</CardTitle>
          <CardDescription>30 dernières exécutions (manuelles + planifiées)</CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">Aucune exécution encore.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Statut</th>
                    <th className="py-2 pr-3">Fichier</th>
                    <th className="py-2 pr-3">Nouveaux</th>
                    <th className="py-2 pr-3">MàJ</th>
                    <th className="py-2 pr-3">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const meta = STATUS_META[log.status] || { label: log.status, color: 'bg-gray-100 text-gray-700', Icon: AlertCircle };
                    return (
                      <tr key={log.id} className="border-b border-gray-100">
                        <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(log.run_at)}</td>
                        <td className="py-2 pr-3">
                          <Badge variant="outline">{log.triggered_by || '—'}</Badge>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${meta.color}`}>
                            <meta.Icon className="w-3 h-3" />
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-2 pr-3 max-w-[160px] truncate">{log.filename || '—'}</td>
                        <td className="py-2 pr-3">{log.imported ?? '—'}</td>
                        <td className="py-2 pr-3">{log.updated ?? '—'}</td>
                        <td className="py-2 pr-3 text-gray-600 max-w-[300px] truncate">{log.message || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MedicielAutoTab;
